import { useState } from 'react';
import { useAppStore } from '../store';
import { motion } from 'framer-motion';
import { Upload, FileText, BookOpen, FileQuestion, CheckCircle, Clock, Search, Filter } from 'lucide-react';

export default function MaterialsPage() {
  const { units, activeSubjectId, uploads, addUpload } = useAppStore();
  const subjectUnits = units.filter(u => u.subjectId === activeSubjectId);
  const [dragOver, setDragOver] = useState(false);
  const [activeFilter, setActiveFilter] = useState<string>('all');

  const handleUpload = () => {
    const newUpload = {
      id: `upload-${Date.now()}`,
      name: `学习资料_${new Date().toLocaleDateString('zh-CN')}.pdf`,
      type: 'notes' as const,
      subjectId: activeSubjectId,
      uploadDate: new Date().toISOString(),
      processed: true,
      extractedUnits: subjectUnits.map(u => u.id),
    };
    addUpload(newUpload);
  };

  const materialCategories = [
    { id: 'foundation', label: '基础知识点', color: 'bg-sage-50 text-sage-dark border-sage-light/40', count: 5, icon: '📗' },
    { id: 'core', label: '核心知识点', color: 'bg-steel-50 text-steel border-steel-light/40', count: 8, icon: '📘' },
    { id: 'advanced', label: '难点知识点', color: 'bg-terracotta-50 text-terracotta border-terracotta-light/40', count: 4, icon: '📕' },
    { id: 'exam', label: 'IB 考点', color: 'bg-cedar-50 text-cedar border-cedar-light/40', count: 12, icon: '⭐' },
    { id: 'common_mistakes', label: '易错点', color: 'bg-terracotta-50 text-terracotta border-terracotta-light/40', count: 6, icon: '⚠️' },
  ];

  return (
    <div className="max-w-6xl mx-auto space-y-6 sm:space-y-8">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-display text-stone-950 tracking-tight">资料分类</h1>
        <p className="text-sm text-stone-400 mt-2 font-body">上传教材或课堂笔记，系统自动识别并分类整理</p>
      </motion.div>

      {/* Upload zone */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); handleUpload(); }}
        className={`relative border-2 border-dashed rounded-3xl p-6 sm:p-10 text-center transition-all cursor-pointer touch-target ${
          dragOver ? 'border-sage bg-sage-50/50' : 'border-stone-200 hover:border-bronze hover:bg-cedar-50/30'
        }`}
        onClick={handleUpload}
      >
        <Upload className={`w-8 h-8 sm:w-10 sm:h-10 mx-auto mb-3 ${dragOver ? 'text-sage' : 'text-stone-300'}`} />
        <p className="text-sm font-medium text-stone-950 font-body">拖拽文件至此处，或点击上传</p>
        <p className="text-xs text-stone-400 mt-1.5 font-body">支持 PDF、Word、图片格式的教材、笔记和试卷</p>
        <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 mt-4 sm:mt-5">
          {['教材', '课堂笔记', '练习册', '历年真题'].map(type => (
            <span key={type} className="text-[10px] px-3 py-1.5 rounded-full bg-stone-100 text-stone-500 font-body">{type}</span>
          ))}
        </div>
      </motion.div>

      {/* Uploaded files */}
      {uploads.length > 0 && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="card-premium p-5">
          <h3 className="text-base font-display mb-3">已上传资料</h3>
          <div className="space-y-2">
            {uploads.map(upload => (
              <div key={upload.id} className="flex items-center gap-3 p-3 rounded-xl bg-linen hover:bg-linen-dark transition-colors">
                <FileText className="w-5 h-5 text-steel" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium truncate font-body">{upload.name}</p>
                  <p className="text-[10px] text-stone-400 font-body">{new Date(upload.uploadDate).toLocaleDateString('zh-CN')}</p>
                </div>
                {upload.processed ? (
                  <span className="flex items-center gap-1 text-[10px] text-sage-dark font-body"><CheckCircle className="w-3 h-3" />已处理</span>
                ) : (
                  <span className="flex items-center gap-1 text-[10px] text-bronze font-body"><Clock className="w-3 h-3" />处理中</span>
                )}
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Material categories */}
      <div>
        <h3 className="text-base font-display mb-4">知识点分类概览</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
          {materialCategories.map((cat, i) => (
            <motion.div
              key={cat.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 + i * 0.06 }}
              className={`rounded-2xl border p-4 sm:p-5 card-hover cursor-pointer touch-target ${cat.color}`}
              onClick={() => setActiveFilter(cat.id)}
            >
              <span className="text-2xl">{cat.icon}</span>
              <p className="text-xs font-semibold mt-2.5 font-body">{cat.label}</p>
              <p className="text-xl font-display mt-1">{cat.count}</p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Knowledge points list */}
      <div className="card-premium p-4 sm:p-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 sm:mb-5">
          <h3 className="text-base font-display">结构化知识点</h3>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5 text-xs bg-linen rounded-xl px-3.5 py-2">
              <Search className="w-3.5 h-3.5 text-stone-400" />
              <input type="text" placeholder="搜索知识点..." className="bg-transparent outline-none text-xs w-32 text-stone-950 font-body placeholder:text-stone-300" />
            </div>
          </div>
        </div>
        {subjectUnits.map(unit => (
          <div key={unit.id} className="mb-5 last:mb-0">
            <h4 className="text-xs font-semibold text-stone-950 mb-2.5 flex items-center gap-2 font-body">
              <BookOpen className="w-3.5 h-3.5 text-steel" />
              {unit.title}
            </h4>
            <div className="space-y-1.5 pl-5">
              {unit.knowledgePoints.map(kp => (
                <div key={kp.id} className="flex items-center gap-2 p-2.5 rounded-xl hover:bg-linen transition-colors">
                  <span className={`text-[9px] px-1.5 py-0.5 rounded-md border font-medium font-body diff-${kp.difficulty}`}>
                    {kp.difficulty === 'foundation' ? '基础' : kp.difficulty === 'core' ? '核心' : '难点'}
                  </span>
                  <span className="text-xs flex-1 font-body">{kp.title}</span>
                  {kp.isExamFocus && <span className="text-[9px] px-1.5 py-0.5 rounded-md bg-cedar-50 text-cedar font-body">考点</span>}
                  {kp.isCommonMistake && <span className="text-[9px] px-1.5 py-0.5 rounded-md bg-terracotta-50 text-terracotta font-body">易错</span>}
                  <div className="w-16 h-1.5 bg-stone-100 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{
                        width: `${kp.mastery}%`,
                        background: kp.mastery > 70 ? 'var(--sage)' : kp.mastery > 40 ? 'var(--bronze)' : 'var(--terracotta)'
                      }}
                    />
                  </div>
                  <span className="text-[10px] text-stone-400 w-8 text-right tabular-nums font-body">{kp.mastery}%</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
